/**
 * Frontend contract for the document delete-impact preview (#5751, ADR-078).
 *
 * Mirrors the backend Go DTO in coronis-apps PR !4230
 * (`apps/pkg/services/provenance_resolution.go` — `DeleteImpact` /
 * `DeleteImpactItem`), served read-only from
 * `POST /documents/{id}/delete-impact`.
 *
 * THIS module is the single isolation point for the backend contract: the DTO
 * shape, the `ReasonCode` union, AND the `CHILD_TYPE_LABELS` record-type keys all
 * live here, so a backend DTO / reason-code / rollup-key shift changes only this
 * file. Shape/comment style follows `DocumentReviewProjection`
 * (src/types/documentTransformation.ts:211).
 */

/**
 * One CLAIMANT-ROOTED entry (or document-level condition) in a delete-impact
 * set. The preview is summarized, not enumerated: each entry is a human-labeled
 * anchor record with a rolled-up count of the records that hang off it.
 *
 * - `label` is the human identifier the UI renders (claimant name, policy
 *   number, …) — NEVER the aegle_id or the raw `id`.
 * - `id` is a UUID (string over the wire), used only as a React key.
 * - `children` is a map of rolled-up COUNTS per record type under this entry
 *   (e.g. `{ claim_items: 12, reimbursements: 2, stoploss_claims: 1 }`),
 *   omitted or empty when the entry stands alone.
 * - `reason` is populated only for Blocked entries — a stable, machine-readable
 *   code the UI branches on.
 */
export interface DeleteEntry {
  type: string;
  id: string;
  label: string;
  reason?: string;
  /** Counts of records under this entry that WOULD BE DELETED, keyed by type. */
  children?: Record<string, number>;
  /**
   * Counts of records under this entry that SURVIVE (kept/scrubbed), keyed by
   * type. Split from `children` so a deletable child under a *kept* claimant is
   * still shown as a deletion rather than hidden in the kept anchor's count.
   */
  children_kept?: Record<string, number>;
}

/**
 * Response payload for the delete-impact endpoint. A read-only PREVIEW: all
 * three sets are always returned so the reviewer sees the full impact ("here's
 * what would be removed — but it's blocked because X"). A non-empty `blocked`
 * set means a cascade delete is not safe. Counts are surfaced separately (they
 * reflect the TRUE totals, which may exceed the rendered/entry array when the
 * server caps the preview) so the frontend can show a "+N more" affordance.
 * `truncated` is a server hint that at least one set was capped.
 */
export interface DeleteImpact {
  delete: DeleteEntry[];
  delete_count: number;
  keep_and_scrub: DeleteEntry[];
  keep_and_scrub_count: number;
  blocked: DeleteEntry[];
  blocked_count: number;
  truncated?: boolean;
}

/**
 * Machine-readable Blocked reason codes (ADR-078). Kept in lock-step with the
 * backend `Reason*` constants in provenance_resolution.go.
 */
export type ReasonCode =
  | "unrecognized-provenance"
  | "client-created"
  | "policy-has-children"
  | "aura-submitted"
  | "carrier-submitted"
  | "asd-breached"
  | "reimbursement-paid"
  | "task-in-progress"
  | "document-in-pipeline"
  | "bulk-origin"
  | "claimant-has-children"
  | "resolution-error"
  | "too-many-records"
  | "asd-recompute-unsupported"
  | "asd-tracking-unreadable"
  | "stoploss-recompute-unsupported";

/**
 * User-facing text for each Blocked reason code. Colocated with the contract so
 * the messaging travels with the codes (pattern: `IN_FLIGHT_LABELS` in
 * DocumentProcessingIndicator.tsx:9). Exhaustive over `ReasonCode` — the `Record`
 * type makes `type-check` fail if a new code is added to the union without a
 * message here, so a known code can never silently render as its raw kebab-case
 * string in front of a user. The dialog still falls back to the raw code at
 * runtime (`?? reason`) for a backend reason not yet added to the union.
 */
export const REASON_CODE_MESSAGES: Record<ReasonCode, string> = {
  "unrecognized-provenance": "The record's origin can't be verified, so it can't be safely removed automatically.",
  "client-created": "This record was created directly by a user, not by loading this document.",
  "policy-has-children": "The policy has dependent records (claims, endorsements, or benefits) that survive.",
  "aura-submitted": "A related claim has already been submitted to Aura for funding.",
  "carrier-submitted": "A related claim has already been marked submitted to the carrier.",
  "asd-breached": "The policy's aggregate specific deductible has been breached.",
  "reimbursement-paid": "A related reimbursement has already been paid.",
  "task-in-progress": "A task tied to this record is still in progress.",
  "document-in-pipeline": "The document is still processing and can't be deleted until it settles.",
  "bulk-origin": "This document came from a bulk (book-of-business) load and can't be cascade-deleted.",
  "claimant-has-children":
    "This claimant has dependent records (claims or reimbursements) that survive, so removing it would orphan them.",
  "resolution-error":
    "The impact couldn't be fully resolved (a lookup failed) — try again; deletion is blocked until it resolves cleanly.",
  "too-many-records":
    "This claimant has too many records to preview safely — deletion is blocked pending a smaller scope.",
  "asd-recompute-unsupported":
    "This policy uses an aggregate specific deductible (ASD); cascade-deleting its records isn't supported yet.",
  "asd-tracking-unreadable":
    "We couldn't read this policy's deductible information, so it can't be removed automatically. Please contact support to have it reviewed.",
  "stoploss-recompute-unsupported":
    "A related stop-loss claim is pharmacy (RX); cascade-deleting these isn't supported yet.",
};

/**
 * Human labels for the rolled-up child COUNT keys in `DeleteEntry.children` /
 * `children_kept`. Keys MUST match the backend record-type strings (singular,
 * per `provenance_resolution.go`); kept here so the backend coupling stays in
 * this one module. A key the backend renames falls back to a naive pluralizer in
 * the UI (no error). Unlike REASON_CODE_MESSAGES, these keys are plain strings
 * (not a union), so the type system does not enforce coverage and there is no
 * contract test against the Go DTO — a renamed key degrades silently and this
 * map is maintained by hand.
 */
export const CHILD_TYPE_LABELS: Record<string, { one: string; other: string }> = {
  claim_item: { one: "claim", other: "claims" },
  reimbursement: { one: "reimbursement", other: "reimbursements" },
  stoploss_claim: { one: "stop-loss claim", other: "stop-loss claims" },
  claimant_policy_snapshot: { one: "snapshot", other: "snapshots" },
  subscriber: { one: "subscriber", other: "subscribers" },
  specific_benefit: { one: "benefit", other: "benefits" },
  endorsement: { one: "endorsement", other: "endorsements" },
  policy: { one: "policy", other: "policies" },
  claimant: { one: "claimant", other: "claimants" },
};
